import React, { useEffect, useRef, useState } from 'react';
import { getProfil, updateProfil, importPdf, importPdfFromStorage, deletePdf, getStellen } from '../api';
import { useConfirm } from './ConfirmDialog';
import { subscribeDokumente } from '../dokumenteStorage';
import { EllipsisVertical } from './animate-ui/icons/EllipsisVertical';
import { UserRound } from './animate-ui/icons/UserRound';
import './ProfilBearbeiten.css';

const cleanText = (value) => String(value || '').trim();

const stelleTitle = (stelle = {}) =>
  stelle.name ||
  stelle.titel ||
  stelle.stellenbezeichnung ||
  stelle.empfaenger_unternehmen ||
  stelle.unternehmen ||
  'Unbenannte Stelle';

const renderUnknownValue = (value) => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value.map(item => typeof item === 'string' ? item : renderUnknownValue(item)).filter(Boolean).join('\n');
  }
  if (typeof value === 'object') {
    return Object.entries(value)
      .map(([key, inner]) => {
        const rendered = renderUnknownValue(inner);
        return rendered ? `${key}: ${rendered}` : '';
      })
      .filter(Boolean)
      .join('\n');
  }
  return String(value);
};

const uniqueStrings = (items) => {
  const seen = new Set();
  return items
    .map(item => cleanText(item))
    .filter(Boolean)
    .filter(item => {
      const key = item.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const profilSektionHatInhalt = (sektion) =>
  (sektion?.typ === 'eintraege' && Object.keys(sektion.eintraege || {}).length > 0) ||
  (sektion?.typ === 'kenntnisse' && (sektion.kenntnisse || []).length > 0) ||
  (sektion?.typ === 'bullets' && (sektion.bullets || []).length > 0);

const makeSektionId = (name, index) =>
  `sek_cvflow_${String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 24) || 'sektion'}_${index}`;

const cvEntryToProfilEntry = (entry = {}, index) => {
  const role = cleanText(entry.rolle || entry.jobtitel);
  const org = cleanText(entry.firma || entry.arbeitgeber);
  const date = cleanText(entry.datum);
  const titleBase = role && org ? `${role} | ${org}` : (role || org || `Eintrag ${index + 1}`);
  const title = date ? `${titleBase} · ${date}` : titleBase;
  const description = (entry.bullets || []).map(cleanText).filter(Boolean).join('\n');
  return [title, description];
};

const cvSkillToProfilText = (skill = {}) => {
  if (typeof skill === 'string') return skill;
  const category = cleanText(skill.kategorie);
  const title = cleanText(skill.titel);
  const description = cleanText(skill.beschreibung);
  return [category, title, description].filter(Boolean).join(' - ');
};

const parseRawCvSection = (name, raw) => {
  const zeilen = String(raw || '').split('\n').map(z => z.trim()).filter(Boolean);
  if (zeilen.length === 0) return null;

  if (zeilen.some(z => z.startsWith('KTITEL:'))) {
    const kenntnisse = [];
    let aktuelleKategorie = '';
    let current = null;
    zeilen.forEach(z => {
      if (z.startsWith('KKATEGORIE:')) {
        aktuelleKategorie = z.slice(11).trim();
        if (current) current.kategorie = aktuelleKategorie;
      } else if (z.startsWith('KTITEL:')) {
        current = { kategorie: aktuelleKategorie, titel: z.slice(7).trim(), beschreibung: '' };
        kenntnisse.push(cvSkillToProfilText(current));
      } else if (z.startsWith('KBESCHR:') && current) {
        current.beschreibung = z.slice(8).trim();
        kenntnisse[kenntnisse.length - 1] = cvSkillToProfilText(current);
      }
    });
    return { name, typ: 'kenntnisse', kenntnisse: uniqueStrings(kenntnisse) };
  }

  const hatFirma = zeilen.some(z => z.startsWith('FIRMA:'));
  const hatEintrag = hatFirma || zeilen.some(z => z.startsWith('DATUM:') || z.startsWith('JOBTITEL:'));
  if (hatEintrag) {
    const entries = [];
    let current = null;
    zeilen.forEach(z => {
      if (hatFirma && z.startsWith('FIRMA:')) {
        current = { firma: z.slice(6).trim(), bullets: [] };
        entries.push(current);
      } else if (!hatFirma && z.startsWith('DATUM:')) {
        current = { datum: z.slice(6).trim(), bullets: [] };
        entries.push(current);
      } else if (current) {
        if (z.startsWith('DATUM:')) current.datum = z.slice(6).trim();
        else if (z.startsWith('ROLLE:')) current.rolle = z.slice(6).trim();
        else if (z.startsWith('JOBTITEL:')) current.jobtitel = z.slice(9).trim();
        else if (z.startsWith('ARBEITGEBER:')) current.arbeitgeber = z.slice(12).trim();
        else if (z.startsWith('BULLET:')) current.bullets.push(z.slice(7).trim());
      }
    });
    return { name, typ: 'eintraege', eintraege: Object.fromEntries(entries.map(cvEntryToProfilEntry)) };
  }

  return {
    name,
    typ: 'bullets',
    bullets: zeilen.map(z => z.startsWith('BULLET:') ? z.slice(7).trim() : z).filter(Boolean),
  };
};

const cvDataSectionToProfilSection = (section = {}) => {
  const name = cleanText(section.name || section.titel || section.typ) || 'Sektion';
  if (Array.isArray(section.eintraege)) {
    return { name, typ: 'eintraege', eintraege: Object.fromEntries(section.eintraege.map(cvEntryToProfilEntry)) };
  }
  if (Array.isArray(section.kenntnisse)) {
    return { name, typ: 'kenntnisse', kenntnisse: uniqueStrings(section.kenntnisse.map(cvSkillToProfilText)) };
  }
  if (Array.isArray(section.bullets)) {
    return { name, typ: 'bullets', bullets: uniqueStrings(section.bullets.map(String)) };
  }
  return parseRawCvSection(name, renderUnknownValue(section.inhalt));
};

const cvflowStelleToProfilSections = (stelle = {}) => {
  const cvData = stelle.cv_data;
  if (cvData?.sektionen?.length) {
    return {
      profilText: cleanText(cvData.profil_text),
      sektionen: cvData.sektionen.map(cvDataSectionToProfilSection).filter(profilSektionHatInhalt),
    };
  }

  const raw = cleanText(stelle.cv_rohtext).replace(/\s*---\s*$/, '').trim();
  if (!raw) return { profilText: '', sektionen: [] };

  const teile = raw.split(/\n?## /);
  const profilTeil = cleanText(teile[0]);
  const profilText = profilTeil.startsWith('PROFIL:') ? profilTeil.slice(7).trim() : profilTeil;
  const sektionen = teile.slice(1)
    .map(teil => {
      const zeilen = teil.split('\n');
      const name = cleanText(zeilen[0]) || 'Sektion';
      return parseRawCvSection(name, zeilen.slice(1).join('\n'));
    })
    .filter(profilSektionHatInhalt);

  return { profilText, sektionen };
};

const mergeProfilSektionen = (existing = [], imported = []) => {
  const merged = existing
    .filter(profilSektionHatInhalt)
    .map((sektion, index) => ({ ...sektion, reihenfolge: index }));

  imported.forEach((sektion, importIndex) => {
    const target = merged.find(item =>
      item.name.trim().toLowerCase() === sektion.name.trim().toLowerCase() &&
      item.typ === sektion.typ,
    );

    if (!target) {
      merged.push({
        id: makeSektionId(sektion.name, merged.length + importIndex),
        name: sektion.name,
        typ: sektion.typ,
        must_have: false,
        reihenfolge: merged.length,
        eintraege: sektion.typ === 'eintraege' ? { ...(sektion.eintraege || {}) } : undefined,
        kenntnisse: sektion.typ === 'kenntnisse' ? [...(sektion.kenntnisse || [])] : undefined,
        bullets: sektion.typ === 'bullets' ? [...(sektion.bullets || [])] : undefined,
      });
      return;
    }

    if (sektion.typ === 'eintraege') {
      target.eintraege = { ...(target.eintraege || {}), ...(sektion.eintraege || {}) };
    } else if (sektion.typ === 'kenntnisse') {
      target.kenntnisse = uniqueStrings([...(target.kenntnisse || []), ...(sektion.kenntnisse || [])]);
    } else {
      target.bullets = uniqueStrings([...(target.bullets || []), ...(sektion.bullets || [])]);
    }
  });

  return merged.map((sektion, index) => ({ ...sektion, reihenfolge: index }));
};

const importCvflowStelleInProfil = (profil, stelle) => {
  const imported = cvflowStelleToProfilSections(stelle);
  return {
    ...profil,
    beschreibung: cleanText(profil?.beschreibung) || imported.profilText,
    sektionen: mergeProfilSektionen(profil?.sektionen, imported.sektionen),
    pdf_importiert_am: new Date().toISOString(),
    pdf_dateiname: `CVFlow Lebenslauf - ${stelleTitle(stelle)}.pdf`,
  };
};

// ── Generisches Eintrags-Formular (strukturiert: Stelle, Firma, Von–Bis, Beschreibung) ──
function parseTitelKey(key) {
  if (!key) return { stelle: '', firma: '', von: '', bis: '' };
  const pipeIdx = key.indexOf(' | ');
  const stelle = pipeIdx >= 0 ? key.slice(0, pipeIdx) : key;
  const rest = pipeIdx >= 0 ? key.slice(pipeIdx + 3) : '';
  const dotIdx = rest.indexOf(' · ');
  let firma = '', datePart = '';
  if (dotIdx >= 0) {
    firma = rest.slice(0, dotIdx);
    datePart = rest.slice(dotIdx + 3);
  } else if (rest.includes(' – ')) {
    datePart = rest;
  } else {
    firma = rest;
  }
  const dashIdx = datePart.indexOf(' – ');
  const von = dashIdx >= 0 ? datePart.slice(0, dashIdx) : datePart;
  const bis = dashIdx >= 0 ? datePart.slice(dashIdx + 3) : '';
  return { stelle, firma, von, bis };
}

function GenericEintragForm({ eintrag, onSave, onAbbrechen }) {
  const parsed = parseTitelKey(eintrag?.key || '');
  const [stelle, setStelle] = useState(parsed.stelle);
  const [firma, setFirma] = useState(parsed.firma);
  const [von, setVon] = useState(parsed.von);
  const [bis, setBis] = useState(parsed.bis);
  const [beschreibung, setBeschreibung] = useState(eintrag?.value || '');
  const inputRef = useRef(null);
  useEffect(() => { if (inputRef.current) inputRef.current.focus({ preventScroll: true }); }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!stelle.trim()) return;
    const sub = [firma.trim(), [von.trim(), bis.trim()].filter(Boolean).join(' – ')].filter(Boolean).join(' · ');
    const key = sub ? `${stelle.trim()} | ${sub}` : stelle.trim();
    onSave(key, beschreibung);
  };

  return (
    <form onSubmit={onSubmit} className="pb-entry-form">
      <div className="pb-add-sek-entry">
        <input ref={inputRef} className="pb-input pb-add-sek-stelle" value={stelle} onChange={e => setStelle(e.target.value)} placeholder="Stelle / Position" required />
        <div className="pb-add-sek-subtitle-row">
          <input className="pb-input pb-add-sek-sub" value={firma} onChange={e => setFirma(e.target.value)} placeholder="Firma / Organisation" />
          <input className="pb-input pb-add-sek-sub pb-add-sek-date" value={von} onChange={e => setVon(e.target.value)} placeholder="Von" />
          <span className="pb-add-sek-dash">–</span>
          <input className="pb-input pb-add-sek-sub pb-add-sek-date" value={bis} onChange={e => setBis(e.target.value)} placeholder="Bis" />
        </div>
        <textarea className="pb-textarea pb-add-sek-desc" rows={3} value={beschreibung} onChange={e => setBeschreibung(e.target.value)} placeholder="Tätigkeiten, Stichpunkte…" />
        <div className="pb-form-actions">
          <button type="button" className="pb-btn-ghost" onClick={onAbbrechen}>Abbrechen</button>
          <button type="submit" className="pb-btn-pill" disabled={!stelle.trim()}>Hinzufügen</button>
        </div>
      </div>
    </form>
  );
}

const DE_MONATE = { jan:0,feb:1,mär:2,mar:2,apr:3,mai:4,jun:5,jul:6,aug:7,sep:8,okt:9,nov:10,dez:11 };
function vonZuSortKey(key) {
  const { von } = parseTitelKey(key);
  if (!von || von.toLowerCase() === 'heute') return Infinity;
  const parts = von.toLowerCase().replace('.','').split(/\s+/);
  const monat = DE_MONATE[parts[0]?.slice(0,3)] ?? -1;
  const jahr = parseInt(parts[1] ?? parts[0]) || 0;
  return jahr * 12 + monat;
}

// ── Eintraege-Sektion (Dict: Titel → Beschreibung) ────────────────
function EintraegeBearbeiten({ sektion, onChange, neuerEintrag, setNeuerEintrag }) {
  const confirm = useConfirm();
  const [bearbeiteKey, setBearbeiteKey] = useState(null);
  // ponytail: sort by start date desc — Object insertion order from PDF import is unreliable
  const eintraege = Object.entries(sektion.eintraege || {}).sort((a, b) => vonZuSortKey(b[0]) - vonZuSortKey(a[0]));
  const onSave = (titel, beschreibung) => {
    const neu = { ...(sektion.eintraege || {}) };
    if (bearbeiteKey && bearbeiteKey !== titel) delete neu[bearbeiteKey];
    neu[titel] = beschreibung;
    onChange({ eintraege: neu }); setNeuerEintrag(false); setBearbeiteKey(null);
  };
  const onLoeschen = async (key) => {
    if (!await confirm('Eintrag wirklich löschen?')) return;
    const neu = { ...(sektion.eintraege || {}) }; delete neu[key]; onChange({ eintraege: neu });
  };
  const parseEntry = (key) => {
    if (key.includes(' | ')) { const [t, rest] = key.split(' | '); return { titel: t, subtitle: rest }; }
    return { titel: key, subtitle: '' };
  };
  return (
    <div className="pb-entries">
      {eintraege.map(([key, value]) => {
        const { titel, subtitle } = parseEntry(key);
        if (bearbeiteKey === key) {
          return <GenericEintragForm key={key} eintrag={{ key, value }} onSave={onSave} onAbbrechen={() => setBearbeiteKey(null)} />;
        }
        return (
          <div key={key} className="pb-card">
            <div className="pb-card-title">{titel}</div>
            {subtitle && <div className="pb-card-subtitle">{subtitle}</div>}
            {value && (
              <ul className="pb-card-bullets">
                {String(value).split('\n').filter(l => l.trim()).map((line, i) => (
                  <li key={i} className="pb-card-bullet">{line.trim().replace(/^[-•·–]\s*/, '')}</li>
                ))}
              </ul>
            )}
            <div className="pb-card-actions">
              <button className="pb-card-btn" onClick={() => setBearbeiteKey(key)}>Bearbeiten</button>
              <button className="pb-card-btn pb-card-btn--del" onClick={() => onLoeschen(key)}>Löschen</button>
            </div>
          </div>
        );
      })}
      {neuerEintrag && <GenericEintragForm eintrag={null} onSave={onSave} onAbbrechen={() => setNeuerEintrag(false)} />}
    </div>
  );
}

// ── Kenntnisse-Sektion (Liste von Strings → Chips) ────────────────
function KenntnisseBearbeiten({ sektion, onChange, neuerEintrag, setNeuerEintrag, initialInputWert }) {
  const confirm = useConfirm();
  const [input, setInput] = useState(initialInputWert || '');
  const inputRef = useRef(null);
  const kenntnisse = sektion.kenntnisse || [];
  useEffect(() => { if (neuerEintrag && inputRef.current) inputRef.current.focus({ preventScroll: true }); }, [neuerEintrag]);
  const onAdd = (e) => {
    e.preventDefault();
    const val = input.trim();
    if (!val || kenntnisse.includes(val)) return;
    onChange({ kenntnisse: [...kenntnisse, val] }); setInput('');
    setNeuerEintrag(false);
  };
  const onLoeschen = async (item) => {
    if (!await confirm(`„${item}" wirklich löschen?`)) return;
    onChange({ kenntnisse: kenntnisse.filter(k => k !== item) });
  };
  return (
    <div>
      <div className="pb-chips">
        {kenntnisse.map(item => (
          <div key={item} className="pb-chip"><span>{item}</span><button className="pb-chip-del" onClick={() => onLoeschen(item)}>✕</button></div>
        ))}
      </div>
      {neuerEintrag && (
        <form onSubmit={onAdd} className="pb-dyn-input-row">
          <input ref={inputRef} className="pb-input pb-dyn-input" value={input} onChange={e => setInput(e.target.value)} placeholder="Kenntnisse hinzufügen…" />
          <button type="button" className="pb-dyn-input-btn pb-dyn-input-btn--cancel" onClick={() => { setNeuerEintrag(false); setInput(''); }}>✕</button>
          <button type="submit" className="pb-dyn-input-btn">{initialInputWert ? '✓' : '+'}</button>
        </form>
      )}
    </div>
  );
}

// ── Bullets-Sektion (Liste von Stichpunkten) ──────────────────────
function BulletsBearbeiten({ sektion, onChange, neuerEintrag, setNeuerEintrag }) {
  const confirm = useConfirm();
  const [input, setInput] = useState('');
  const inputRef = useRef(null);
  const bullets = sektion.bullets || [];
  useEffect(() => { if (neuerEintrag && inputRef.current) inputRef.current.focus({ preventScroll: true }); }, [neuerEintrag]);
  const onAdd = (e) => {
    e.preventDefault();
    const val = input.trim();
    if (!val) return;
    onChange({ bullets: [...bullets, val] }); setInput('');
    setNeuerEintrag(false);
  };
  const onLoeschen = async (idx) => {
    if (!await confirm('Eintrag wirklich löschen?')) return;
    onChange({ bullets: bullets.filter((_, i) => i !== idx) });
  };
  return (
    <div className="pb-bullets-wrap">
      {bullets.map((b, idx) => (
        <div key={idx} className="pb-bullet-row">
          <span className="pb-bullet-dot">·</span>
          <span className="pb-bullet-text">{b}</span>
          <button className="pb-chip-del" onClick={() => onLoeschen(idx)}>✕</button>
        </div>
      ))}
      {neuerEintrag && (
        <form onSubmit={onAdd} className="pb-dyn-input-row">
          <input ref={inputRef} className="pb-input pb-dyn-input" value={input} onChange={e => setInput(e.target.value)} placeholder="Stichpunkt hinzufügen…" />
          <button type="button" className="pb-dyn-input-btn pb-dyn-input-btn--cancel" onClick={() => { setNeuerEintrag(false); setInput(''); }}>✕</button>
          <button type="submit" className="pb-dyn-input-btn">+</button>
        </form>
      )}
    </div>
  );
}

// ── PDF-Löschen-Modal (Nur PDF oder alles zurücksetzen) ───────────
function PdfLoeschenModal({ pdfDateiname, onNurPdf, onMitSektionen, onAbbrechen }) {
  return (
    <div className="pdf-del-overlay" onClick={onAbbrechen}>
      <div className="pdf-del-modal" onClick={e => e.stopPropagation()}>
        <div className="pdf-del-header">
          <span className="pdf-del-title">{pdfDateiname || 'Lebenslauf entfernen'}</span>
          <button className="dok-picker-close" onClick={onAbbrechen}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        <div className="pdf-del-btn-group">
          <button className="pdf-del-btn pdf-del-btn--secondary" onClick={onNurPdf}>
            <div className="pdf-del-btn-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                <path d="M18 13l-6 6-3-3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="pdf-del-btn-text">
              <span className="pdf-del-btn-label">Nur PDF entfernen</span>
              <span className="pdf-del-btn-sub">Sektionen bleiben erhalten</span>
            </div>
          </button>
          <button className="pdf-del-btn pdf-del-btn--danger" onClick={onMitSektionen}>
            <div className="pdf-del-btn-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M3 6h18M8 6V4h8v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="pdf-del-btn-text">
              <span className="pdf-del-btn-label">PDF + alle Sektionen löschen</span>
              <span className="pdf-del-btn-sub">Komplett zurücksetzen</span>
            </div>
          </button>
          <button className="pdf-del-btn pdf-del-btn--ghost" onClick={onAbbrechen}>Abbrechen</button>
        </div>
      </div>
    </div>
  );
}

// ── Ghost-Vorschau (leeres Profil) ────────────────────────────────
function GhostSektionenPreview({ show }) {
  const [visible, setVisible] = useState(show);
  const [exiting, setExiting] = useState(false);
  const [entering, setEntering] = useState(show);

  // Eingangs-Animation beim ersten Render abklingen lassen (nur wenn initial sichtbar)
  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => setEntering(false), 620);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!show && visible && !exiting) {
      setExiting(true);
      setEntering(false);
      const t = setTimeout(() => { setVisible(false); setExiting(false); }, 620);
      return () => clearTimeout(t);
    }
    if (show && !visible) {
      setVisible(true);
      setEntering(true);
      const t = setTimeout(() => setEntering(false), 620);
      return () => clearTimeout(t);
    }
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible) return null;

  const cardClass = (exitIdx, enterIdx) => {
    if (exiting)  return `pb-ghost-card pb-ghost-card--exit  pb-ghost-card--exit-${exitIdx}`;
    if (entering) return `pb-ghost-card pb-ghost-card--enter pb-ghost-card--enter-${enterIdx}`;
    return 'pb-ghost-card';
  };

  return (
    <div className="pb-ghost-wrap">
      <div className={cardClass(2, 2)}>
        <div className="pb-ghost-header">
          <span className="pb-ghost-name">Muster-Berufserfahrung</span>
        </div>
        <div className="pb-ghost-entry">
          <span className="pb-ghost-entry-title">Industriemechaniker · Muster GmbH · 02/2021 – heute</span>
          <span className="pb-ghost-entry-body">• Wartung und Reparatur von Produktionsanlagen<br/>• Mitarbeit bei der Optimierung von Fertigungsprozessen</span>
        </div>
      </div>
      <div className={cardClass(1, 1)}>
        <div className="pb-ghost-header">
          <span className="pb-ghost-name">Muster-Sprachen</span>
        </div>
        <div className="pb-ghost-pills">
          <span className="pb-ghost-pill">Deutsch (Muttersprache)</span>
          <span className="pb-ghost-pill">Englisch (B2)</span>
          <span className="pb-ghost-pill">Spanisch (A1)</span>
        </div>
      </div>
      <div className={cardClass(0, 0)}>
        <div className="pb-ghost-header">
          <span className="pb-ghost-name">Muster-Bildungsweg</span>
        </div>
        <div className="pb-ghost-entry">
          <span className="pb-ghost-entry-title">Ausbildung Industriemechaniker · Berufsschule Musterstadt · 2018 – 2021</span>
          <span className="pb-ghost-entry-body">Abschlussnote: 2,0</span>
        </div>
      </div>
    </div>
  );
}

// ── Dokumentenpicker (Lebenslauf aus eigenen Docs wählen) ─────────
function DokumentenPicker({ onAuswaehlen, onSchliessen }) {
  const [dokumente, setDokumente] = useState([]);
  const [stellen, setStellen] = useState([]);
  const [dokumenteGeladen, setDokumenteGeladen] = useState(false);
  const [stellenGeladen, setStellenGeladen] = useState(false);

  useEffect(() => {
    const unsub = subscribeDokumente((docs) => {
      setDokumente(docs.filter(d => d.filename?.toLowerCase().endsWith('.pdf')));
      setDokumenteGeladen(true);
    }, () => setDokumenteGeladen(true));
    return unsub;
  }, []);

  useEffect(() => {
    let aktiv = true;
    getStellen()
      .then(res => {
        if (!aktiv) return;
        setStellen(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (aktiv) setStellen([]);
      })
      .finally(() => {
        if (aktiv) setStellenGeladen(true);
      });
    return () => { aktiv = false; };
  }, []);

  const formatDatum = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const cvflowLebenslaeufe = stellen
    .filter(s => (s.quelle || 'cvflow') !== 'extern')
    .filter(s => s.cv_data || s.cv_rohtext)
    .sort((a, b) =>
      new Date(b.generiert_am || b.erstellt_am || 0) -
      new Date(a.generiert_am || a.erstellt_am || 0));

  const laedt = !dokumenteGeladen || !stellenGeladen;
  const istLeer = dokumente.length === 0 && cvflowLebenslaeufe.length === 0;

  return (
    <div className="dok-picker-overlay" onClick={onSchliessen}>
      <div className="dok-picker-modal" onClick={e => e.stopPropagation()}>
        <div className="dok-picker-header">
          <span className="dok-picker-title">Dokument auswählen</span>
          <button className="dok-picker-close" onClick={onSchliessen}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        {laedt ? (
          <div className="dok-picker-empty">Lade Dokumente…</div>
        ) : istLeer ? (
          <div className="dok-picker-empty">Keine Lebensläufe in deinen Dokumenten gefunden.</div>
        ) : (
          <div className="dok-picker-list">
            {cvflowLebenslaeufe.map(stelle => (
              <button key={`cvflow-${stelle.id}`} className="dok-picker-item" onClick={() => onAuswaehlen({ typ: 'cvflow', stelle })}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="dok-picker-icon">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                  <path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 13h8M8 17h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
                <div className="dok-picker-info">
                  <span className="dok-picker-name">{stelleTitle(stelle)}</span>
                  <span className="dok-picker-date">
                    CVFlow-Lebenslauf{formatDatum(stelle.generiert_am || stelle.erstellt_am) ? ` · ${formatDatum(stelle.generiert_am || stelle.erstellt_am)}` : ''}
                  </span>
                </div>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="dok-picker-arrow">
                  <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            ))}
            {dokumente.map(d => (
              <button key={d.id} className="dok-picker-item" onClick={() => onAuswaehlen({ typ: 'pdf', dok: d })}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="dok-picker-icon">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                  <path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div className="dok-picker-info">
                  <span className="dok-picker-name">{d.filename}</span>
                  <span className="dok-picker-date">
                    Hochgeladene PDF{d.uploadedAt ? ` · ${formatDatum(d.uploadedAt)}` : ''}
                  </span>
                </div>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="dok-picker-arrow">
                  <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/// ── Formular: Neue Sektion anlegen ────────────────────────────────
function SektionHinzufuegenForm({ onAdd, onAbbrechen }) {
  const [name, setName]             = useState('');
  const [stelle, setStelle]         = useState('');
  const [firma, setFirma]           = useState('');
  const [von, setVon]               = useState('');
  const [bis, setBis]               = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    let eintraege = {};
    if (stelle.trim()) {
      const sub = [firma.trim(), [von.trim(), bis.trim()].filter(Boolean).join(' – ')].filter(Boolean).join(' · ');
      const key = sub ? `${stelle.trim()} | ${sub}` : stelle.trim();
      eintraege[key] = beschreibung.trim();
    }
    onAdd(name.trim(), 'eintraege', { eintraege });
  };

  return (
    <form onSubmit={onSubmit} className="pb-add-sek-form">
      <input
        ref={inputRef}
        className="pb-input pb-add-sek-input pb-add-sek-name"
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder="Berufserfahrung, Praktikum, Ausbildung…"
        required
      />

      <div className="pb-add-sek-entry">
        <input className="pb-input pb-add-sek-stelle" value={stelle} onChange={e => setStelle(e.target.value)} placeholder="Stelle / Position" />
        <div className="pb-add-sek-subtitle-row">
          <input className="pb-input pb-add-sek-sub" value={firma} onChange={e => setFirma(e.target.value)} placeholder="Firma / Organisation" />
          <input className="pb-input pb-add-sek-sub pb-add-sek-date" value={von} onChange={e => setVon(e.target.value)} placeholder="Von" />
          <span className="pb-add-sek-dash">–</span>
          <input className="pb-input pb-add-sek-sub pb-add-sek-date" value={bis} onChange={e => setBis(e.target.value)} placeholder="Bis" />
        </div>
        <textarea
          className="pb-textarea pb-add-sek-desc"
          rows={2}
          value={beschreibung}
          onChange={e => setBeschreibung(e.target.value)}
          placeholder="Tätigkeiten, Stichpunkte…"
        />
      </div>

      <div className="pb-form-actions">
        <button type="button" className="pb-btn-ghost" onClick={onAbbrechen}>Abbrechen</button>
        <button type="submit" className="pb-btn-pill" disabled={!name.trim()}>Anlegen</button>
      </div>
    </form>
  );
}

// ── Dynamische Sektion (Wrapper für alle drei Typen) ──────────────
function DynSektionBearbeiten({ sektion, onChange, onLoeschen, onUmbenennen, onAllesLeeren, dragZustand, onDragStart, onDragEnter, initialInputWert }) {
  const confirm = useConfirm();
  const [editName, setEditName] = useState(false);
  const [newName, setNewName] = useState(sektion.name);
  const [neuerEintrag, setNeuerEintrag] = useState(!!initialInputWert);
  const [offen, setOffen] = useState(false);
  const selfRef = useRef(null);
  useEffect(() => {
    if (initialInputWert && selfRef.current) selfRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, []); // ponytail: einmalig beim Mount, dep-array leer ist Absicht
  const [menuOffen, setMenuOffen] = useState(false);
  const menuRef = useRef(null);
  const handleContentChange = (updatedFields) => onChange({ ...sektion, ...updatedFields });

  useEffect(() => {
    if (dragZustand !== null && dragZustand !== undefined) setOffen(dragZustand);
  }, [dragZustand]);

  const hatInhalt =
    (sektion.typ === 'eintraege'  && Object.keys(sektion.eintraege  || {}).length > 0) ||
    (sektion.typ === 'kenntnisse' && (sektion.kenntnisse || []).length > 0) ||
    (sektion.typ === 'bullets'    && (sektion.bullets    || []).length > 0);

  useEffect(() => {
    if (!menuOffen) return;
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOffen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOffen]);

  // SVG-Icon je Sektionstyp
  const SektionIcon = () => {
    if (sektion.typ === 'kenntnisse') return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      </svg>
    );
    if (sektion.typ === 'bullets') return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    );
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.8"/>
        <path d="M7 8h10M7 12h10M7 16h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
      </svg>
    );
  };

  return (
    <div
      ref={selfRef}
      className={`pb-section${offen ? '' : ' pb-section--collapsed'}`}
      onMouseEnter={() => onDragEnter && onDragEnter(sektion.id)}
    >
      <div
        className="pb-section-head"
        style={{ cursor: 'pointer' }}
        onMouseDown={e => {
          if (editName || e.target.closest('.pb-section-actions')) return;
          e.preventDefault();
          const neu = !offen;
          setOffen(neu);
          onDragStart && onDragStart(sektion.id, neu);
        }}
      >
        <div className="pb-section-head-left">
          <span className={`pb-section-chevron${offen ? ' pb-section-chevron--open' : ''}`}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          {editName ? (
            <form onSubmit={e => { e.preventDefault(); onUmbenennen(sektion.id, newName.trim() || sektion.name); setEditName(false); }} className="pb-rename-form">
              <input className="pb-input pb-rename-input" value={newName} onChange={e => setNewName(e.target.value)} autoFocus />
              <button type="submit" className="pb-rename-ok">✓</button>
              <button type="button" className="pb-rename-cancel" onClick={() => setEditName(false)}>✕</button>
            </form>
          ) : (
            <span className="pb-section-label">{sektion.name.toUpperCase()}</span>
          )}
        </div>

        {!editName && (
          <div className="pb-section-actions">
            {!neuerEintrag && (
              <button className="pb-add-btn" onClick={() => setNeuerEintrag(true)}>+ Hinzufügen</button>
            )}
            {/* Immer gerendert → Platz reserviert → Hinzufügen bleibt ausgerichtet */}
            <div className={`pb-more-wrap${menuOffen ? ' pb-more-wrap--active' : ''}`} ref={menuRef}>
              <button
                className="pb-dots-btn"
                title="Mehr Optionen"
                onClick={() => setMenuOffen(v => !v)}
              >
                <EllipsisVertical size={20} animateOnHover style={{ display: 'block' }} />
              </button>
              {menuOffen && (
                <div className="stellen-dropdown pb-more-dropdown">
                  <button
                    className="stellen-dropdown-item"
                    disabled={!hatInhalt}
                    onClick={async () => { if (!hatInhalt) return; setMenuOffen(false); if (await confirm(`Alle Einträge in „${sektion.name}" löschen?`)) onAllesLeeren(sektion.id); }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                      <path d="M3 6h18M8 6V4h8v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Inhalt leeren</span>
                  </button>
                  <>
                    <button className="stellen-dropdown-item" onClick={() => { setMenuOffen(false); setNewName(sektion.name); setEditName(true); }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Umbenennen</span>
                    </button>
                    <div className="stellen-dropdown-divider" />
                    <button className="stellen-dropdown-item danger" onClick={async () => { setMenuOffen(false); if (await confirm(`Sektion „${sektion.name}" wirklich löschen?`)) onLoeschen(sektion.id); }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                        <path d="M3 6h18M8 6V4h8v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Sektion löschen</span>
                    </button>
                  </>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {offen && sektion.typ === 'eintraege'  && (hatInhalt || neuerEintrag) && <EintraegeBearbeiten  sektion={sektion} onChange={handleContentChange} neuerEintrag={neuerEintrag} setNeuerEintrag={setNeuerEintrag} />}
      {offen && sektion.typ === 'kenntnisse' && (hatInhalt || neuerEintrag) && <KenntnisseBearbeiten sektion={sektion} onChange={handleContentChange} neuerEintrag={neuerEintrag} setNeuerEintrag={setNeuerEintrag} initialInputWert={initialInputWert} />}
      {offen && sektion.typ === 'bullets'    && (hatInhalt || neuerEintrag) && <BulletsBearbeiten    sektion={sektion} onChange={handleContentChange} neuerEintrag={neuerEintrag} setNeuerEintrag={setNeuerEintrag} />}
    </div>
  );
}

// ── Basisprofil (8 Felder × 12,5 % = 100 %) ─────────────────────
const BASIS_FELDER = [
  { key: 'vorname',    label: 'Vorname' },
  { key: 'nachname',   label: 'Nachname' },
  { key: 'email',      label: 'E-Mail' },
  { key: 'telefon',    label: 'Telefon' },
  { key: 'strasse',    label: 'Straße' },
  { key: 'hausnummer', label: 'Hausnr.' },
  { key: 'plz',        label: 'PLZ' },
  { key: 'ort',        label: 'Wohnort' },
];

function berechneBasisPct_pb(profil) {
  if (!profil) return 0;
  return Math.round(BASIS_FELDER.filter(f => profil[f.key] && String(profil[f.key]).trim().length > 0).length * 12.5);
}

function BasisProfilStatus({ profil }) {
  const pct = berechneBasisPct_pb(profil);
  if (pct >= 100) return null; // bei 100 % überflüssig
  return (
    <div className="pb-basis-card">
      <div className="pb-basis-header">
        <div className="pb-basis-header-left">
          <span className="pb-basis-label">Basisprofil</span>
          <span className="pb-basis-sub">Pflichtangaben für Bewerbungen</span>
        </div>
        <span className="pb-basis-pct">{pct}%</span>
      </div>
      <div className="pb-basis-track"><div className="pb-basis-fill" style={{ width: `${pct}%` }} /></div>
      <div className="pb-basis-checklist">
        {BASIS_FELDER.map(f => {
          const fertig = profil?.[f.key] && String(profil[f.key]).trim().length > 0;
          return (
            <span key={f.key} className={`pb-basis-check${fertig ? ' pb-basis-check--done' : ''}`}>
              <span className="pb-basis-check-icon">{fertig ? '✓' : '○'}</span>{f.label}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ── Profil-Optimierung ────────────────────────────────────────────
const STATUS_OPTIONEN = [
  'Schüler/in', 'Student/in', 'Ausbildung', 'Absolvent/in',
  'Berufseinsteiger/in', 'Berufstätig', 'Selbstständig', 'Freelancer',
];

const STATUS_DETAILS_CONFIG = {
  'Student/in':        [{ key: 'universitaet', label: 'Universität / Hochschule' }, { key: 'studiengang', label: 'Studiengang' }],
  'Schüler/in':        [{ key: 'schule', label: 'Schule' }, { key: 'abschluss', label: 'Angestrebter Abschluss' }],
  'Ausbildung':        [{ key: 'ausbildungsberuf', label: 'Ausbildungsberuf' }, { key: 'ausbildungsbetrieb', label: 'Ausbildungsbetrieb' }],
  'Berufstätig':       [{ key: 'unternehmen', label: 'Aktuelles Unternehmen' }, { key: 'position', label: 'Position / Rolle' }],
};

// Häufig genutzte Sprachen im deutschsprachigen Raum — immer oben
const SPRACHEN_PRIORITAET = [
  'Deutsch', 'Englisch', 'Türkisch', 'Arabisch', 'Russisch',
  'Polnisch', 'Französisch', 'Spanisch', 'Italienisch', 'Griechisch',
  'Kroatisch', 'Portugiesisch', 'Niederländisch',
];

// Vollständige Liste — wird alphabetisch sortiert angezeigt (ohne Prioritäts-Sprachen)
const SPRACHEN_ALLE = [
  'Afrikaans', 'Akan', 'Albanisch', 'Amharisch', 'Arabisch', 'Armenisch',
  'Assamesisch', 'Aserbaidschanisch', 'Awadhi', 'Azeri',
  'Belutschisch', 'Bengali', 'Bhojpuri', 'Bulgarisch', 'Burmesisch',
  'Cebuano', 'Chewa', 'Chittagonisch', 'Chhattisgarhi',
  'Dänisch', 'Dekhini', 'Deutsch',
  'Englisch',
  'Finnisch', 'Französisch', 'Fulani',
  'Gan Chinesisch', 'Georgisch', 'Griechisch', 'Gujarati',
  'Haitianisches Kreol', 'Hakka Chinesisch', 'Haryanvi', 'Hausa', 'Hebräisch', 'Hiligaynon', 'Hindi', 'Hmong',
  'Igbo', 'Ilocano', 'Indonesisch', 'Italienisch',
  'Javanisch', 'Japanisch', 'Jin Chinesisch',
  'Kantonesisch', 'Kannada', 'Kasachisch', 'Khmer', 'Kinyarwanda', 'Kirundi', 'Kirgisisch', 'Konkani', 'Koreanisch', 'Kroatisch', 'Kurdisch',
  'Laotisch', 'Lettisch', 'Litauisch', 'Luganda',
  'Maduresisch', 'Magahi', 'Maithili', 'Malagasy', 'Malayalam', 'Malaiisch', 'Mandarin Chinesisch', 'Marathi', 'Marwari', 'Min Nan Chinesisch', 'Mongolisch', 'Mossi',
  'Nepali', 'Niederländisch', 'Nord Min Chinesisch', 'Norwegisch',
  'Odia', 'Oromo', 'Ost Min Chinesisch',
  'Paschto', 'Persisch', 'Polnisch', 'Portugiesisch', 'Punjabi',
  'Quechua',
  'Rumänisch', 'Russisch',
  'Saraiki', 'Schwedisch', 'Serbo-Kroatisch', 'Serbisch', 'Shona', 'Sindhi', 'Singhalesisch', 'Slowakisch', 'Slowenisch', 'Somali', 'Spanisch', 'Sundanesisch', 'Swahili', 'Sylheti',
  'Tagalog', 'Tamilisch', 'Telugu', 'Thai', 'Tigrinya', 'Tschechisch', 'Turkmenisch', 'Türkisch',
  'Uighurisch', 'Ukrainisch', 'Ungarisch', 'Urdu', 'Usbekisch',
  'Vietnamesisch',
  'Weißrussisch', 'Wu Chinesisch',
  'Xhosa', 'Xiang Chinesisch',
  'Yoruba',
  'Zhuang', 'Zulu',
].sort((a, b) => a.localeCompare(b, 'de'));

const STAATSANGEHOERIGKEITEN = [
  'Ägyptisch', 'Äthiopisch', 'Afghanisch', 'Albanisch', 'Algerisch',
  'Amerikanisch', 'Angolanisch', 'Argentinisch', 'Armenisch', 'Aserbaidschanisch',
  'Australisch', 'Bangladeschisch', 'Belgisch', 'Beninisch', 'Bolivianisch',
  'Bosnisch', 'Brasilianisch', 'Britisch', 'Bulgarisch', 'Burundisch',
  'Cebuano', 'Chilenisch', 'Chinesisch', 'Dänisch', 'Deutsch',
  'Ecuadorianisch', 'Eritreisch', 'Finnisch', 'Französisch', 'Ghanaisch',
  'Griechisch', 'Guatemaltekisch', 'Haitianisch', 'Honduranisch', 'Indisch',
  'Indonesisch', 'Irakisch', 'Iranisch', 'Irländisch', 'Israelisch',
  'Italienisch', 'Ivorisch', 'Japanisch', 'Jemenitisch', 'Jordanisch',
  'Kambodschanisch', 'Kanadisch', 'Kasachisch', 'Kenianisch', 'Kirgisisch',
  'Kolumbianisch', 'Kongolesisch', 'Koreanisch', 'Kroatisch', 'Kubanisch',
  'Laotisch', 'Lettisch', 'Libanesisch', 'Libysisch', 'Litauisch',
  'Luxemburgisch', 'Madagassisch', 'Malaysisch', 'Malisch', 'Marokkanisch',
  'Mexikanisch', 'Moldauisch', 'Mongolisch', 'Mosambikanisch', 'Myanmarisch',
  'Namibisch', 'Nepalesisch', 'Neuseeländisch', 'Nicaraguanisch', 'Niederländisch',
  'Nigerianisch', 'Nigrisch', 'Nordkoreanisch', 'Norwegisch', 'Österreichisch',
  'Pakistanisch', 'Paraguayisch', 'Peruanisch', 'Philippinisch', 'Polnisch',
  'Portugiesisch', 'Ruandisch', 'Rumänisch', 'Russisch', 'Saudi-Arabisch',
  'Schwedisch', 'Schweizerisch', 'Senegalesisch', 'Serbisch', 'Slowakisch',
  'Slowenisch', 'Somalisch', 'Spanisch', 'Sri-Lankisch', 'Sudanesisch',
  'Südafrikanisch', 'Syrisch', 'Tadschikisch', 'Tansanisch', 'Thailändisch',
  'Togisch', 'Tschechisch', 'Tunesisch', 'Türkisch', 'Turkmenisch',
  'Ugandisch', 'Ukrainisch', 'Ungarisch', 'Uruguayisch', 'Usbekisch',
  'Venezolanisch', 'Vietnamesisch', 'Weißrussisch', 'Zimbabwisch', 'Zypriotisch',
];

function parseSprachen(wert) {
  if (Array.isArray(wert)) return wert;
  if (typeof wert === 'string' && wert.trim().length > 0) {
    return wert.split(',').map(s => s.trim()).filter(Boolean);
  }
  return [];
}

function berechneOptimierungsPct(profil) {
  if (!profil) return 0;
  let p = 0;
  if (profil.beschreibung && String(profil.beschreibung).trim().length > 10) p += 20;
  const statusArr = Array.isArray(profil.aktueller_status) ? profil.aktueller_status : (profil.aktueller_status ? [profil.aktueller_status] : []);
  if (statusArr.length > 0) p += 15;
  if (parseSprachen(profil.sprachen).length > 0) p += 15;
  if (profil.linkedin && String(profil.linkedin).trim()) p += 10;
  if (profil.geburtsdatum && String(profil.geburtsdatum).trim()) p += 8;
  if (profil.staatsangehoerigkeit && String(profil.staatsangehoerigkeit).trim()) p += 8;
  if (profil.verfuegbarkeit && String(profil.verfuegbarkeit).trim()) p += 8;
  if (profil.portfolio && String(profil.portfolio).trim()) p += 8;
  if (profil.github && String(profil.github).trim()) p += 8;
  return Math.min(p, 100);
}

function StatusAuswahl({ wert, details, onChange, onDetailsChange }) {
  const aktiv = Array.isArray(wert) ? wert : (wert ? [wert] : []);
  const toggle = (opt) => {
    if (aktiv.includes(opt)) onChange(aktiv.filter(s => s !== opt));
    else onChange([...aktiv, opt]);
  };
  const alleDetailFelder = aktiv.flatMap(s =>
    (STATUS_DETAILS_CONFIG[s] || []).map(f => ({ ...f, statusKey: s }))
  );
  return (
    <div className="pb-status-wrap">
      <div className="pb-status-optionen">
        {STATUS_OPTIONEN.map(opt => (
          <button
            key={opt}
            type="button"
            className={`pb-status-option${aktiv.includes(opt) ? ' pb-status-option--aktiv' : ''}`}
            onClick={() => toggle(opt)}
          >
            {opt}
          </button>
        ))}
      </div>
      {alleDetailFelder.length > 0 && (
        <div className="pb-status-details">
          {alleDetailFelder.map(f => (
            <div key={`${f.statusKey}_${f.key}`} className="pb-field">
              <label className="pb-field-label">{f.label}</label>
              <input
                className="pb-input"
                value={details?.[f.key] || ''}
                placeholder={f.label}
                onChange={e => onDetailsChange({ ...(details || {}), [f.key]: e.target.value })}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const VERF_PRESETS = ['ab sofort', 'flexibel'];
function VerfuegbarkeitAuswahl({ wert, onChange }) {
  const isDatum = wert && !VERF_PRESETS.includes(wert);
  const activeMode = VERF_PRESETS.includes(wert) ? wert : (isDatum ? 'datum' : '');
  return (
    <div className="pb-status-wrap">
      <div className="pb-status-optionen">
        {VERF_PRESETS.map(p => (
          <button key={p} type="button"
            className={`pb-status-option${activeMode === p ? ' pb-status-option--aktiv' : ''}`}
            onClick={() => onChange(activeMode === p ? '' : p)}>
            {p}
          </button>
        ))}
        <button type="button"
          className={`pb-status-option${activeMode === 'datum' ? ' pb-status-option--aktiv' : ''}`}
          onClick={() => onChange(isDatum ? '' : new Date().toISOString().slice(0, 10))}>
          Datum wählen
        </button>
      </div>
      {(activeMode === 'datum' || isDatum) && (
        <div className="pb-status-details" style={{ gridTemplateColumns: '1fr' }}>
          <div className="pb-field">
            <label className="pb-field-label">Ab wann verfügbar?</label>
            <input className="pb-input" type="date" value={isDatum ? wert : ''}
              onChange={e => onChange(e.target.value)} />
          </div>
        </div>
      )}
    </div>
  );
}

function SprachenAuswahl({ wert, onChange }) {
  const ausgewaehlt = parseSprachen(wert);
  const [suche, setSuche] = useState('');
  const [offen, setOffen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!offen) return;
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOffen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [offen]);
  const sucheLower = suche.toLowerCase();
  const passt = (s) => !ausgewaehlt.includes(s) && s.toLowerCase().startsWith(sucheLower);
  // Prioritäts-Sprachen zuerst, dann Rest alphabetisch
  const prioritaet = SPRACHEN_PRIORITAET.filter(passt);
  const rest = SPRACHEN_ALLE.filter(s => passt(s));
  const gefiltert = [...prioritaet, ...rest];
  const hinzufuegen = (sprache) => { onChange([...ausgewaehlt, sprache]); setSuche(''); setOffen(false); };
  const entfernen = (sprache) => onChange(ausgewaehlt.filter(s => s !== sprache));
  return (
    <div className="pb-sprachen-wrap" ref={wrapRef}>
      {ausgewaehlt.length > 0 && (
        <div className="pb-sprachen-liste">
          {ausgewaehlt.map(s => (
            <div key={s} className="pb-sprache-item">
              <span className="pb-sprache-name">{s}</span>
              <button className="pb-sprache-del" type="button" onClick={() => entfernen(s)}>✕</button>
            </div>
          ))}
        </div>
      )}
      <div className="pb-sprachen-suche-wrap">
        <input
          className="pb-input"
          value={suche}
          placeholder="Sprache suchen und hinzufügen…"
          onChange={e => { setSuche(e.target.value); setOffen(true); }}
          onFocus={() => setOffen(true)}
        />
        {offen && gefiltert.length > 0 && (
          <div className="pb-sprachen-dropdown">
            {suche === '' && prioritaet.length > 0 && (
              <div className="pb-sprachen-gruppe-label">Häufig verwendet</div>
            )}
            {prioritaet.map(s => (
              <button key={s} type="button" className="pb-sprachen-vorschlag" onClick={() => hinzufuegen(s)}>{s}</button>
            ))}
            {suche === '' && rest.length > 0 && prioritaet.length > 0 && (
              <div className="pb-sprachen-gruppe-label pb-sprachen-gruppe-label--sep">Alle Sprachen</div>
            )}
            {rest.map(s => (
              <button key={s} type="button" className="pb-sprachen-vorschlag" onClick={() => hinzufuegen(s)}>{s}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SearchableSelect({ value, options, placeholder, onChange }) {
  const [suche, setSuche] = useState('');
  const [offen, setOffen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!offen) return;
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOffen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [offen]);
  const gefiltert = options.filter(o => o.toLowerCase().startsWith(suche.toLowerCase()));
  const waehlen = (opt) => { onChange(opt); setSuche(''); setOffen(false); };
  return (
    <div className="pb-sprachen-wrap" ref={wrapRef}>
      {value && (
        <div className="pb-sprache-item">
          <span className="pb-sprache-name">{value}</span>
          <button className="pb-sprache-del" type="button" onClick={() => { onChange(''); setSuche(''); }}>✕</button>
        </div>
      )}
      <div className="pb-sprachen-suche-wrap">
        <input
          className="pb-input"
          value={suche}
          placeholder={value ? 'Ändern…' : (placeholder || 'Suchen und auswählen…')}
          onChange={e => { setSuche(e.target.value); setOffen(true); }}
          onFocus={() => setOffen(true)}
        />
        {offen && gefiltert.length > 0 && (
          <div className="pb-sprachen-dropdown">
            {gefiltert.slice(0, 20).map(o => (
              <button key={o} type="button" className="pb-sprachen-vorschlag" onClick={() => waehlen(o)}>{o}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function OptimierungsTeaser({ profil, onClick }) {
  const pct = berechneOptimierungsPct(profil);
  const statusArr = Array.isArray(profil?.aktueller_status) ? profil.aktueller_status : (profil?.aktueller_status ? [profil.aktueller_status] : []);
  const fehlend = [
    !(profil?.beschreibung && String(profil.beschreibung).trim().length > 10) && 'Beschreibung',
    statusArr.length === 0 && 'Status',
    parseSprachen(profil?.sprachen).length === 0 && 'Sprachen',
    !profil?.linkedin?.trim() && 'LinkedIn',
  ].filter(Boolean);
  return (
    <div className="pb-opt-teaser" onClick={onClick} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}>
      <div className="pb-opt-teaser-left">
        <div className="pb-opt-teaser-top">
          <span className="pb-opt-teaser-label">Profil-Optimierung</span>
          <span className="pb-opt-teaser-badge">Optional</span>
        </div>
        <div className="pb-opt-teaser-track"><div className="pb-opt-teaser-fill" style={{ width: `${pct}%` }} /></div>
        {fehlend.length > 0 && <span className="pb-opt-teaser-sub">{fehlend.join(' · ')} noch offen</span>}
        {pct >= 100 && <span className="pb-opt-teaser-sub pb-opt-teaser-sub--done">Vollständig optimiert</span>}
      </div>
      <div className="pb-opt-teaser-right">
        <span className="pb-opt-teaser-pct">{pct}%</span>
        <span className="pb-opt-teaser-arrow">→</span>
      </div>
    </div>
  );
}

function OptimierungsCard({ profil }) {
  const pct = berechneOptimierungsPct(profil);
  return (
    <div className="pb-opt-card">
      <div className="pb-opt-header">
        <span className="pb-opt-label">Optimierungs-Score</span>
        <span className="pb-opt-pct">{pct}%</span>
      </div>
      <div className="pb-opt-track"><div className="pb-opt-fill" style={{ width: `${pct}%` }} /></div>
      {pct >= 100 && <p className="pb-opt-done">Profil vollständig optimiert.</p>}
    </div>
  );
}

// ── Haupt-Komponente ──────────────────────────────────────────────
function ProfilBearbeiten({ dateiname, onZurueck, onLoeschen, onGespeichert, initialSektion, initialSektionWert }) {
  const confirm = useConfirm();
  const [profil, setProfil] = useState(null);
  const [laedt, setLaedt] = useState(true);
  const [speichert, setSpeichert] = useState(false);
  const [gespeichert, setGespeichert] = useState(false);
  const [fehler, setFehler] = useState(null);
  const [pdfLaedt, setPdfLaedt] = useState(false);
  const [pdfErfolg, setPdfErfolg] = useState(false);
  const [pdfFehler, setPdfFehler] = useState(null);
  const [unteransicht, setUnteransicht] = useState('haupt');
  const [loeschenBestaetigung, setLoeschenBestaetigung] = useState(false);
  const [stammOffen, setStammOffen] = useState(false);
  const [holdPct, setHoldPct] = useState(0);
  const dragRef = useRef({ aktiv: false, zielZustand: null });
  const [dragMap, setDragMap] = useState({});

  const onDragStart = (id, zielZustand) => {
    dragRef.current = { aktiv: true, zielZustand };
    setDragMap({ [id]: zielZustand });
  };
  const onDragEnter = (id) => {
    if (!dragRef.current.aktiv) return;
    setDragMap(m => ({ ...m, [id]: dragRef.current.zielZustand }));
  };
  useEffect(() => {
    const stop = () => { dragRef.current = { aktiv: false, zielZustand: null }; setDragMap({}); };
    window.addEventListener('mouseup', stop);
    return () => window.removeEventListener('mouseup', stop);
  }, []);
  const [addSektionOffen, setAddSektionOffen] = useState(false);
  const [pdfDragOver, setPdfDragOver] = useState(false);
  const [pdfModusWahl, setPdfModusWahl] = useState(false);
  const [dokPickerOffen, setDokPickerOffen] = useState(false);
  const [pdfLoeschenModalOffen, setPdfLoeschenModalOffen] = useState(false);
  const [dbHinweisOffen, setDbHinweisOffen] = useState(
    () => localStorage.getItem('pb_db_hinweis_geschlossen') !== '1'
  );
  const pdfDropRef = useRef(null);
  const pdfInputRef = useRef(null);
  const holdIntervalRef = useRef(null);
  const holdStartRef = useRef(null);
  const saveTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const isSavingRef = useRef(false);
  const prevProfilRef = useRef(null);
  const onGespeichertRef = useRef(onGespeichert);
  useEffect(() => { onGespeichertRef.current = onGespeichert; }, [onGespeichert]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);


  // ── Debounced auto-save (500 ms) ────────────────────────────────
  useEffect(() => {
    if (!profil) return;
    if (prevProfilRef.current === null) { prevProfilRef.current = profil; return; }
    if (prevProfilRef.current === profil) return;
    prevProfilRef.current = profil;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    const snapshot = profil;
    // ponytail: reschedule if a save is still running; captures latest snapshot via closure
    const attempt = async () => {
      if (isSavingRef.current) { saveTimerRef.current = setTimeout(attempt, 300); return; }
      isSavingRef.current = true;
      try {
        const res = await updateProfil(dateiname, snapshot);
        prevProfilRef.current = res.data;
        if (mountedRef.current) {
          setGespeichert(true);
          setTimeout(() => { if (mountedRef.current) setGespeichert(false); }, 2000);
        }
        if (onGespeichertRef.current) onGespeichertRef.current(dateiname);
      } catch {
        if (mountedRef.current) setFehler('Profil konnte nicht gespeichert werden.');
      } finally {
        isSavingRef.current = false;
      }
    };
    saveTimerRef.current = setTimeout(attempt, 500);
    // No cleanup return — timer must survive unmount to flush pending saves
  }, [profil, dateiname]);

  const startHold = () => {
    holdStartRef.current = Date.now();
    holdIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - holdStartRef.current;
      const pct = Math.min(100, (elapsed / 3000) * 100);
      setHoldPct(pct);
      if (pct >= 100) {
        clearInterval(holdIntervalRef.current);
        holdIntervalRef.current = null;
        setLoeschenBestaetigung(true);
        setHoldPct(0);
      }
    }, 30);
  };
  const cancelHold = () => {
    if (holdIntervalRef.current) { clearInterval(holdIntervalRef.current); holdIntervalRef.current = null; }
    setHoldPct(0);
  };

  useEffect(() => {
    getProfil(dateiname)
      .then((res) => { setProfil(res.data); })
      .catch(() => setFehler('Profil konnte nicht geladen werden.'))
      .finally(() => setLaedt(false));
  }, [dateiname]);

  // Klick außerhalb der LEBENSLAUF-Karte schließt die Auswahl
  useEffect(() => {
    if (!pdfModusWahl) return;
    const handler = (e) => {
      if (pdfDropRef.current && !pdfDropRef.current.contains(e.target)) {
        setPdfModusWahl(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pdfModusWahl]);

  useEffect(() => {
    if (laedt || !initialSektion || initialSektionWert) return; // DynSektionBearbeiten scrolls when wert is set
    const el = document.querySelector(`[data-sektion="${initialSektion}"]`);
    if (el) el.scrollIntoView({ block: 'start' });
  }, [laedt, initialSektion, initialSektionWert]);


const onPersoenlichChange = (field, value) => { setProfil(p => ({ ...p, [field]: value })); };
  const onSektionChange = (id, updatedSek) => {
    setProfil(p => ({ ...p, sektionen: p.sektionen.map(s => s.id === id ? updatedSek : s) }));
  };
  const onSektionLoeschen = (id) => {
    setProfil(p => ({ ...p, sektionen: p.sektionen.filter(s => s.id !== id) }));
  };
  const onSektionUmbenennen = (id, neuerName) => {
    setProfil(p => ({ ...p, sektionen: p.sektionen.map(s => s.id === id ? { ...s, name: neuerName } : s) }));
  };
  const onSektionHinzufuegen = (name, typ, initialData = {}) => {
    const id = `sek_${Math.random().toString(36).slice(2, 8)}`;
    const base = { id, name, typ, must_have: false };
    const sek = typ === 'eintraege' ? { ...base, eintraege: initialData.eintraege || {} }
              : typ === 'kenntnisse' ? { ...base, kenntnisse: [] }
              : { ...base, bullets: [] };
    setProfil(p => ({ ...p, sektionen: [...(p.sektionen || []), { ...sek, reihenfolge: (p.sektionen || []).length }] }));
    setAddSektionOffen(false);
  };

  const uploadPdfDatei = async (datei) => {
    if (!datei) return;
    setPdfLaedt(true); setPdfErfolg(false); setPdfFehler(null);
    try {
      const res = await importPdf(dateiname, datei);
      setProfil(res.data.profil);
      setPdfErfolg(true);
      setTimeout(() => setPdfErfolg(false), 3000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setPdfFehler(detail || 'Import fehlgeschlagen. Bitte erneut versuchen.');
    } finally { setPdfLaedt(false); }
  };

  const onPdfUpload = async (e) => {
    const datei = e.target.files[0];
    e.target.value = '';
    await uploadPdfDatei(datei);
  };

  const onPdfLoeschen = (e) => {
    e.stopPropagation();
    setPdfLoeschenModalOffen(true);
  };

  const onPdfNurLoeschen = async () => {
    setPdfLoeschenModalOffen(false);
    try {
      await deletePdf(dateiname);
      setProfil(p => { const n = { ...p }; delete n.pdf_importiert_am; delete n.pdf_dateiname; return n; });
    } catch { /* Fehler ignorieren — Profil bleibt unverändert */ }
  };

  const onPdfUndSektionenLoeschen = async () => {
    setPdfLoeschenModalOffen(false);
    try {
      await deletePdf(dateiname);
      setProfil(p => {
        const n = { ...p };
        delete n.pdf_importiert_am;
        delete n.pdf_dateiname;
        n.sektionen = [];
        return n;
      });
    } catch { /* Fehler ignorieren */ }
  };

  const onSektionAllesLeeren = (id) => {
    setProfil(p => ({
      ...p,
      sektionen: p.sektionen.map(s => {
        if (s.id !== id) return s;
        return s.typ === 'eintraege'  ? { ...s, eintraege: {} }
             : s.typ === 'kenntnisse' ? { ...s, kenntnisse: [] }
             :                          { ...s, bullets: [] };
      }),
    }));
  };

  useEffect(() => {
    // Verhindert, dass Firefox/Chrome Dateien im Browser öffnet
    const preventBrowserDefault = (e) => e.preventDefault();
    document.addEventListener('dragover', preventBrowserDefault);
    document.addEventListener('drop', preventBrowserDefault);
    return () => {
      document.removeEventListener('dragover', preventBrowserDefault);
      document.removeEventListener('drop', preventBrowserDefault);
    };
  }, []);

  useEffect(() => {
    const el = pdfDropRef.current;
    if (!el) return;
    const onEnter = (e) => { e.preventDefault(); setPdfDragOver(true); setPdfModusWahl(false); };
    const onOver  = (e) => { e.preventDefault(); setPdfDragOver(true); };
    const onLeave = (e) => { if (!el.contains(e.relatedTarget)) setPdfDragOver(false); };
    const onDrop  = (e) => {
      e.preventDefault(); e.stopPropagation();
      setPdfDragOver(false);
      const datei = e.dataTransfer?.files?.[0];
      if (datei) uploadPdfDatei(datei);
    };
    el.addEventListener('dragenter', onEnter);
    el.addEventListener('dragover',  onOver);
    el.addEventListener('dragleave', onLeave);
    el.addEventListener('drop',      onDrop);
    return () => {
      el.removeEventListener('dragenter', onEnter);
      el.removeEventListener('dragover',  onOver);
      el.removeEventListener('dragleave', onLeave);
      el.removeEventListener('drop',      onDrop);
    };
  // profil !== null stellt sicher, dass der Effect neu läuft sobald
  // das Element nach dem Laden gerendert wurde (vorher war pdfDropRef.current null)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateiname, profil !== null]);

  const onDokumentAuswaehlen = async (auswahl) => {
    setDokPickerOffen(false);
    setPdfLaedt(true); setPdfErfolg(false); setPdfFehler(null);
    try {
      if (auswahl.typ === 'pdf') {
        const res = await importPdfFromStorage(dateiname, auswahl.dok.storagePath, auswahl.dok.filename);
        setProfil(res.data.profil);
      } else {
        const nextProfil = importCvflowStelleInProfil(profil, auswahl.stelle);
        const res = await updateProfil(dateiname, nextProfil);
        if (mountedRef.current) {
          prevProfilRef.current = res.data;
          isSavingRef.current = true;
          setProfil(res.data);
          isSavingRef.current = false;
        }
        localStorage.removeItem('profile_cache');
      }
      setPdfErfolg(true);
      setTimeout(() => setPdfErfolg(false), 3000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setPdfFehler(detail || 'Dokument konnte nicht importiert werden. Bitte erneut versuchen.');
    } finally {
      setPdfLaedt(false);
    }
  };

  const onSpeichern = async () => {
    setSpeichert(true); setFehler(null);
    try {
      const res = await updateProfil(dateiname, profil);
      localStorage.removeItem('profile_cache');
      if (mountedRef.current) {
        prevProfilRef.current = res.data;
        isSavingRef.current = true;
        setProfil(res.data);
        isSavingRef.current = false;
        setGespeichert(true);
        setTimeout(() => { if (mountedRef.current) setGespeichert(false); }, 2000);
      }
      if (onGespeichert) onGespeichert(dateiname);
    } catch { setFehler('Profil konnte nicht gespeichert werden.'); }
    finally { setSpeichert(false); }
  };

  if (laedt) return <div className="pb-loading">Wird geladen…</div>;
  if (!profil) return <div className="pb-loading pb-loading--err">{fehler}</div>;

  // Welche Sektion soll den vorausgefüllten Keyword-Input bekommen?
  const targetSektionId = initialSektionWert
    ? ((profil.sektionen || []).find(s => s.typ === 'kenntnisse' && s.name.toLowerCase() === 'kenntnisse') ||
       (profil.sektionen || []).find(s => s.typ === 'kenntnisse'))?.id
    : null;

  // ── Optimierungs-Unteransicht ────────────────────────────────────
  if (unteransicht === 'optimierung') {
    return (
      <div className="pb-wrap">
        <div className="pb-header">
          <button className="pb-zurueck-opt" onClick={() => setUnteransicht('haupt')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M9.57 5.93L3.5 12l6.07 6.07" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M20.5 12H3.67" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Zurück
          </button>
          <h2 className="pb-title">Profil-Optimierung</h2>
          <p className="pb-subtitle">Je mehr du hier angibst, desto besser kann die KI deine Bewerbungen auf dich zuschneiden.</p>
        </div>

        <div className="pb-body">
          <OptimierungsCard profil={profil} />

          {/* KI-Hinweis */}
          <div className="pb-ki-hinweis">
            <p className="pb-ki-hinweis-text">
              <strong>Warum das wichtig ist:</strong> Die KI nutzt diese Angaben, um Bewerbungen präzise auf dich zuzuschneiden. Besonders wenn eine Stelle nicht exakt zu deinem Werdegang passt, können Details — z.&nbsp;B. Sprachen, dein Hintergrund oder deine Verfügbarkeit — entscheidend sein.
            </p>
          </div>

          {/* Soft Skills */}
          {(() => {
            const softSek = (profil.sektionen || []).find(s => s.name.toLowerCase() === 'soft skills');
            if (!softSek) return null;
            return (
              <DynSektionBearbeiten
                sektion={softSek}
                onChange={(updated) => onSektionChange(softSek.id, updated)}
                onLoeschen={onSektionLoeschen}
                onUmbenennen={onSektionUmbenennen}
                onAllesLeeren={onSektionAllesLeeren}
              />
            );
          })()}

          {/* Profilbeschreibung — oben, weil zentral */}
          <div className="pb-section">
            <div className="pb-section-head">
              <div className="pb-section-head-left">
                <span className="pb-section-icon">✍️</span>
                <span className="pb-section-label">PROFILBESCHREIBUNG</span>
              </div>
            </div>
            <textarea
              className="pb-textarea pb-textarea--beschreibung"
              rows={6}
              value={profil.beschreibung || ''}
              onChange={(e) => onPersoenlichChange('beschreibung', e.target.value)}
              placeholder="Beschreibe dich kurz — wer du bist, was dich ausmacht und wonach du suchst. Die KI verwendet diesen Text als Basis für Anschreiben."
              maxLength={500}
            />
            <span className="pb-char-counter">{500 - (profil.beschreibung || '').length} Zeichen übrig</span>
          </div>

          {/* Aktueller Status */}
          <div className="pb-section">
            <div className="pb-section-head">
              <div className="pb-section-head-left">
                <span className="pb-section-icon">📌</span>
                <span className="pb-section-label">AKTUELLER STATUS</span>
              </div>
            </div>
            <StatusAuswahl
              wert={profil.aktueller_status || []}
              details={profil.status_details || {}}
              onChange={v => onPersoenlichChange('aktueller_status', v)}
              onDetailsChange={v => onPersoenlichChange('status_details', v)}
            />
          </div>

          {/* Sprachen */}
          <div className="pb-section">
            <div className="pb-section-head">
              <div className="pb-section-head-left">
                <span className="pb-section-icon">🌐</span>
                <span className="pb-section-label">SPRACHEN</span>
              </div>
            </div>
            <SprachenAuswahl wert={profil.sprachen} onChange={v => onPersoenlichChange('sprachen', v)} />
          </div>

          {/* Verfügbarkeit */}
          <div className="pb-section">
            <div className="pb-section-head">
              <div className="pb-section-head-left">
                <span className="pb-section-icon">📅</span>
                <span className="pb-section-label">VERFÜGBARKEIT</span>
              </div>
            </div>
            <div className="pb-opt-stack">
              <div className="pb-field">
                <VerfuegbarkeitAuswahl wert={profil.verfuegbarkeit || ''} onChange={v => onPersoenlichChange('verfuegbarkeit', v)} />
              </div>
            </div>
          </div>

          {/* Externe Profile */}
          <div className="pb-section">
            <div className="pb-section-head">
              <div className="pb-section-head-left">
                <span className="pb-section-icon">🔗</span>
                <span className="pb-section-label">EXTERNE PROFILE</span>
              </div>
            </div>
            <p className="pb-ext-info">Diese Links können automatisch in generierte Anschreiben eingebunden werden — du entscheidest für jedes Profil einzeln.</p>
            <div className="pb-opt-stack">
              {[
                { key: 'linkedin', toggleKey: 'linkedin_in_anschreiben', label: 'LinkedIn', placeholder: 'https://linkedin.com/in/deinprofil' },
                { key: 'portfolio', toggleKey: 'portfolio_in_anschreiben', label: 'Portfolio-Website', placeholder: 'https://meinportfolio.de' },
                { key: 'github', toggleKey: 'github_in_anschreiben', label: 'GitHub', placeholder: 'https://github.com/deinname' },
              ].map(({ key, toggleKey, label, placeholder }) => (
                <div key={key} className="pb-ext-field-wrap">
                  <label className="pb-field-label">{label}</label>
                  <div className="pb-ext-inline-row">
                    <input className="pb-input" value={profil[key] || ''} placeholder={placeholder}
                      onChange={e => onPersoenlichChange(key, e.target.value)} />
                    <label className={`pb-ext-toggle${!profil[key]?.trim() ? ' pb-ext-toggle--disabled' : ''}`}>
                      <input type="checkbox"
                        checked={!!profil[toggleKey]}
                        disabled={!profil[key]?.trim()}
                        onChange={e => onPersoenlichChange(toggleKey, e.target.checked)} />
                      <span>Im Anschreiben</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="pb-footer">
          <button className="pb-btn-ghost" onClick={() => setUnteransicht('haupt')}>Zurück</button>
          <button className="pb-btn-pill" onClick={onSpeichern} disabled={speichert}>
            {speichert ? 'Wird gespeichert…' : gespeichert ? '✓ Gespeichert' : 'Speichern'}
          </button>
        </div>
      </div>
    );
  }

  // ── Haupt-Ansicht ────────────────────────────────────────────────
  return (
    <div className="pb-wrap">
      <div className="pb-header">
        <h2 className="pb-title">Profil</h2>
        <p className="pb-subtitle">Trage alle deine Erfahrungen ein, auch wenn sie nicht immer auf den ersten Blick relevant wirken. Je vollständiger dein Profil, desto dynamischer werden deine Lebensläufe: Die KI stellt für jede Bewerbung automatisch den passenden Inhalt zusammen.</p>
      </div>

      <div className="pb-body">
        {/* Basis-Status — nur wenn unvollständig */}
        <BasisProfilStatus profil={profil} />

        {/* Stammdaten — zuerst */}
        <div className="pb-section">
          <div className="pb-section-head pb-section-head--toggle" onClick={() => setStammOffen(v => !v)}>
            <div className="pb-section-head-left">
              <span className="pb-section-icon"><UserRound size={16} animate={stammOffen} /></span>
              <span className="pb-section-label">STAMMDATEN</span>
            </div>
            <span className={`pb-section-chevron${stammOffen ? ' pb-section-chevron--open' : ''}`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          </div>
          <div className={`pb-stamm-collapse${stammOffen ? '' : ' pb-stamm-collapse--closed'}`}>
            <div className="pb-stamm-grid">
              {[
                { key: 'vorname',    label: 'Vorname' },
                { key: 'nachname',   label: 'Nachname' },
                { key: 'email',      label: 'E-Mail Adresse' },
                { key: 'telefon',    label: 'Telefon' },
                { key: 'strasse',    label: 'Straße' },
                { key: 'hausnummer', label: 'Hausnummer' },
                { key: 'plz',        label: 'PLZ' },
                { key: 'ort',        label: 'Ort' },
              ].map((f) => (
                <div key={f.key} className="pb-field">
                  <label className="pb-field-label">{f.label}</label>
                  <input className="pb-input" value={profil[f.key] || ''} onChange={(e) => onPersoenlichChange(f.key, e.target.value)} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* PDF Import — nach den Stammdaten */}
        <div
          ref={pdfDropRef}
          className={`pb-section pb-lv-card${pdfDragOver ? ' pb-lv-card--drag' : ''}`}
          onClick={() => { if (!pdfLaedt) setPdfModusWahl(v => !v); }}
        >
          <input ref={pdfInputRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={onPdfUpload} disabled={pdfLaedt} />

          {/* Border Beam beim Laden */}
          {pdfLaedt && (
            <svg className="pb-lv-beam" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <rect className="pb-lv-beam-line"
                x="0" y="0" width="100%" height="100%"
                rx="14" ry="14"
                fill="none"
                stroke="#FFBC00"
                strokeWidth="12"
                strokeDasharray="100 1200"
                strokeLinecap="round"
                pathLength="1300"
              />
            </svg>
          )}

          {pdfDragOver ? (
            <div className="pb-lv-drop-hint">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                <path d="M14 2v6h6M12 11v6M9 14l3-3 3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Lebenslauf hier ablegen</span>
            </div>
          ) : pdfModusWahl && !pdfLaedt ? (
            <div className="pb-lv-choice" onClick={e => e.stopPropagation()}>
              <button className="pb-lv-choice-btn" onClick={() => { setPdfModusWahl(false); pdfInputRef.current?.click(); }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.8"/>
                  <path d="M8 21h8M12 17v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
                <span>Von meinem Computer</span>
              </button>
              <div className="pb-lv-choice-sep" />
              <button className="pb-lv-choice-btn" onClick={() => { setPdfModusWahl(false); setDokPickerOffen(true); }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                  <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Aus CVflow Dokumenten</span>
              </button>
            </div>
          ) : (
            <div className="pb-lv-inner">
              <div className="pb-lv-left">
                <span className="pb-section-icon" style={{ fontSize: 13 }}>📄</span>
                <span className="pb-section-label">LEBENSLAUF</span>
                {pdfErfolg ? (
                  <span className="pb-pdf-inline-status pb-pdf-inline-status--ok">✓ Importiert</span>
                ) : profil.pdf_importiert_am ? (
                  <span className="pb-pdf-inline-status pb-pdf-inline-status--file">
                    {profil.pdf_dateiname || 'Lebenslauf.pdf'}
                  </span>
                ) : (
                  <span className="pb-pdf-inline-status pb-pdf-inline-status--leer">Noch nicht hochgeladen</span>
                )}
              </div>
              <div className="pb-lv-actions">
                <span className="pb-lv-btn">
                  {pdfLaedt ? 'Wird importiert…' : profil.pdf_importiert_am ? 'Ersetzen' : 'Hochladen'}
                </span>
                {profil.pdf_importiert_am && !pdfLaedt && (
                  <button className="pb-lv-del-btn" onClick={onPdfLoeschen} title="Lebenslauf entfernen">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                      <path d="M3 6h18M8 6V4h8v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}

          {pdfFehler && !pdfModusWahl && (
            <p className="pb-pdf-fehler-inline" style={{ marginTop: 6 }} onClick={e => e.stopPropagation()}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              {pdfFehler}
            </p>
          )}
        </div>

        {dokPickerOffen && (
          <DokumentenPicker
            onAuswaehlen={onDokumentAuswaehlen}
            onSchliessen={() => setDokPickerOffen(false)}
          />
        )}

        {pdfLoeschenModalOffen && (
          <PdfLoeschenModal
            pdfDateiname={profil.pdf_dateiname}
            onNurPdf={onPdfNurLoeschen}
            onMitSektionen={onPdfUndSektionenLoeschen}
            onAbbrechen={() => setPdfLoeschenModalOffen(false)}
          />
        )}

        {/* CV-Sektionen (dynamisch) */}
        <GhostSektionenPreview show={(profil.sektionen || []).length === 0} />
        {(profil.sektionen || [])
          .slice()
          .sort((a, b) => {
            const aK = a.typ === 'kenntnisse' ? 1 : 0;
            const bK = b.typ === 'kenntnisse' ? 1 : 0;
            if (aK !== bK) return aK - bK;
            // Innerhalb der Kenntnisse-Gruppe: „Kenntnisse" immer als letztes
            if (aK === 1) {
              const aLast = a.name.toLowerCase() === 'kenntnisse' ? 1 : 0;
              const bLast = b.name.toLowerCase() === 'kenntnisse' ? 1 : 0;
              if (aLast !== bLast) return aLast - bLast;
            }
            return (a.reihenfolge ?? 999) - (b.reihenfolge ?? 999);
          })
          .map((sek) => (
            <div key={sek.id} data-sektion={sek.name.toLowerCase()} data-sek-typ={sek.typ}>
              <DynSektionBearbeiten
                sektion={sek}
                onChange={(updated) => onSektionChange(sek.id, updated)}
                onLoeschen={onSektionLoeschen}
                onUmbenennen={onSektionUmbenennen}
                onAllesLeeren={onSektionAllesLeeren}
                dragZustand={dragMap[sek.id] ?? null}
                onDragStart={onDragStart}
                onDragEnter={onDragEnter}
                initialInputWert={sek.id === targetSektionId ? initialSektionWert : undefined}
              />
            </div>
          ))
        }

        {/* Sektion hinzufügen */}
        {addSektionOffen ? (
          <div className="pb-section">
            <SektionHinzufuegenForm
              onAdd={onSektionHinzufuegen}
              onAbbrechen={() => setAddSektionOffen(false)}
            />
          </div>
        ) : (
          <button className="pb-neue-sektion-btn" onClick={() => setAddSektionOffen(true)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="rgba(255,255,255,0.9)" strokeWidth="2.8" strokeLinecap="round"/>
            </svg>
            Neue Sektion
          </button>
        )}

        {/* Optimierungs-Teaser */}
        <OptimierungsTeaser profil={profil} onClick={() => setUnteransicht('optimierung')} />

        {fehler && <p className="pb-error">{fehler}</p>}
      </div>

      <div className="pb-footer">
        {onLoeschen && (
          !loeschenBestaetigung ? (
            <button
              className="pb-btn-delete-hold"
              onMouseDown={startHold}
              onMouseUp={cancelHold}
              onMouseLeave={cancelHold}
              onTouchStart={startHold}
              onTouchEnd={cancelHold}
            >
              <span className="pb-btn-delete-hold-fill" style={{ width: `${holdPct}%` }} />
              <span className="pb-btn-delete-hold-content">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, opacity: 0.7 }}>
                  <path d="M21 5.98C17.67 5.65 14.32 5.48 10.98 5.48C9 5.48 7.02 5.58 5.04 5.78L3 5.98" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M8.5 4.97L8.72 3.66C8.88 2.71 9 2 10.69 2H13.31C15 2 15.13 2.75 15.28 3.67L15.5 4.97" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M18.85 9.14L18.2 19.21C18.09 20.78 18 22 15.21 22H8.79C6 22 5.91 20.78 5.8 19.21L5.15 9.14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <span>{holdPct > 0 ? 'Halten…' : 'Profil löschen'}</span>
                <span className="pb-btn-delete-hold-hint">Drei Sekunden gedrückt halten</span>
              </span>
            </button>
          ) : (
            <div className="pb-danger-confirm-mini">
              <span className="pb-danger-confirm-text">Wirklich unwiderruflich löschen?</span>
              <div className="pb-danger-confirm-btns">
                <button className="pb-btn-ghost pb-btn-ghost--xs" onClick={() => setLoeschenBestaetigung(false)}>Abbrechen</button>
                <button className="pb-btn-delete pb-btn-delete--confirm" onClick={onLoeschen}>Ja, löschen</button>
              </div>
            </div>
          )
        )}
        <button className="pb-btn-pill" onClick={onSpeichern} disabled={speichert} style={{ marginLeft: 'auto' }}>
          {speichert ? 'Wird gespeichert…' : gespeichert ? '✓ Gespeichert' : 'Speichern'}
        </button>
      </div>
    </div>
  );
}

export default ProfilBearbeiten;
